/**
 * @type {{
 *  GUIDED_SEARCH: DataLayerEventsType,
 *  JOB_ALERT: DataLayerEventsType,
 *  JOB_APPLY_CTA_CLICK: DataLayerEventsType,
 *  JOB_MERKEN_ADD: DataLayerEventsType,
 *  JOB_MERKEN_REMOVE: DataLayerEventsType,
 *  JOB_SEARCH_CTA_CLICK: DataLayerEventsType,
 *  JOB_SEARCH: DataLayerEventsType,
 *  JOBS_OVERVIEW: DataLayerEventsType,
 *  JOB_VIEW: DataLayerEventsType,
 *  CONTENT_HUB_TEASER_PREVIEW_CLICK: DataLayerEventsType,
 *  CONTENT_HUB_TEASER_MEHR_ANZEIGEN_CLICK: DataLayerEventsType,
 *  NAV_CLICK: DataLayerEventsType,
 * }}
 */
export const dataLayerEvents = {
  GUIDED_SEARCH: 'guided_search',
  JOB_ALERT: 'job_alert',
  JOB_APPLY_CTA_CLICK: 'job_apply_cta_click',
  JOB_MERKEN_ADD: 'job_merken_add',
  JOB_MERKEN_REMOVE: 'job_merken_remove',
  JOB_SEARCH_CTA_CLICK: 'job_search_cta_click',
  JOB_SEARCH: 'jobsuche',
  JOBS_OVERVIEW: 'jobs_overview',
  JOB_VIEW: 'view_job',
  CONTENT_HUB_TEASER_PREVIEW_CLICK: 'content_hub_teaser_preview_click',
  CONTENT_HUB_TEASER_MEHR_ANZEIGEN_CLICK:
    'content_hub_teaser_mehr_anzeigen_click',
  NAV_CLICK: 'nav_click',
};

/**
 * @type {{
 * TEASER_CLICK: DataLayerActionsType,
 * FORM_INTERACTION: DataLayerActionsType
 * JOB_ALERT_SUBSCRIBE: DataLayerActionsType
 * }}
 */
export const dataLayerActions = {
  TEASER_CLICK: 'teaser_click',
  FORM_INTERACTION: 'form_interaction',
  JOB_ALERT_SUBSCRIBE: 'job_alert_abonnieren',
};

/**
 * @typedef {Object} SelectedFilter
 * @property {string} organization
 * @property {string} job_type
 */

let gtmReady = false;
let listenersBound = false;

/**
 * @type {Record<string, unknown>[]}
 */
const queuedEvents = [];

const flushQueue = () => {
  if (typeof window === 'undefined') return;
  if (!gtmReady) return;
  if (!window.dataLayer) return;

  while (queuedEvents.length > 0) {
    const event = queuedEvents.shift();
    if (event) {
      window.dataLayer.push(event);
    }
  }
};

export const markGtmReady = () => {
  if (typeof window === 'undefined') return;

  gtmReady = true;
  flushQueue();
  window.dispatchEvent(new CustomEvent('gtm:ready'));
};

export const isGtmReady = () => {
  if (typeof window === 'undefined') return false;
  return gtmReady && !!window.dataLayer;
};

/**
 * @param {Object} props
 * @param {() => boolean} props.hasAnalyticsConsent
 * @returns {void}
 */
export const bindConsentListeners = ({ hasAnalyticsConsent }) => {
  if (typeof window === 'undefined') return;
  if (listenersBound) return;

  const tryFlush = () => {
    if (hasAnalyticsConsent() && window.dataLayer) {
      gtmReady = true;
      flushQueue();
    }
  };

  window.addEventListener('OneTrustGroupsUpdated', tryFlush);
  window.addEventListener('gtm:ready', tryFlush);

  listenersBound = true;
};

/**
 * @param {Object} param
 * @param {DataLayerEventsType} [param.event]
 * @param {DataLayerActionsType | undefined} [param.action]
 * @param {string | undefined} [param.label]
 * @param {string | undefined} [param.info]
 * @param {string | undefined} [param.location_country]
 * @param {string | undefined} [param.location_city]
 * @param {string | undefined} [param.job_id]
 * @param {string | undefined} [param.job_title]
 * @param {string | undefined} [param.organization]
 * @param {string | undefined} [param.filter_organization]
 * @param {string | undefined} [param.language]
 * @param {string | undefined} [param.pagetype]
 * @param {string[] | undefined} [param.filter_job_type]
 * @param {string[] | undefined} [param.selected_filters]
 * @param {* | undefined} [param.payload]
 * @param {Context} [param.context]
 * @returns {boolean}
 */
export const pushEvent = ({
  event,
  action,
  label,
  info,
  organization,
  filter_job_type,
  filter_organization,
  location_country,
  location_city,
  selected_filters,
  job_id,
  job_title,
  payload = {},
  context,
  language,
  pagetype,
}) => {
  // @ts-ignore
  if (typeof window === 'undefined') {
    return false;
  }

  const eventData = {
    event,
    ...{ ...(action && { action }) },
    ...{ ...(label && { label }) },
    ...{ ...(info && { info }) },
    ...{ ...(organization && { organization }) },
    ...{ ...(location_country && { location_country }) },
    ...{ ...(location_city && { location_city }) },
    ...{ ...(selected_filters && { selected_filters }) },
    ...{ ...(job_id && { job_id }) },
    ...{ ...(job_title && { job_title }) },
    ...{ ...(filter_job_type && { filter_job_type }) },
    ...{ ...(filter_organization && { filter_organization }) },
    ...{ ...(context && { context }) },
    ...{ ...(language && { language }) },
    ...{ ...(pagetype && { pagetype }) },
    ...payload,
  };

  if (gtmReady && window.dataLayer) {
    window.dataLayer.push(eventData);
    return true;
  }

  queuedEvents.push(eventData);
  return false;
};
